import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { keyMapItem, KeyDisplayOverride, LayerKeysMap, Macro } from '@rapoo/device-sdk'
import { defaultReportError } from '@/composables/useErrorLogger'
import { useDeviceStore } from '@/stores/device'
import { toast } from '@rapoo/element'
import { useLocaleStore } from '@/stores'
import type { KeyPlatform, KeySetting, LayoutKey } from '../types'
import {
  buildSdkKeyMapPayloadFromSetting,
  buildRestoreKeyMapPayload,
  parseLayerMapToKeySettings,
} from '../utils/keyMapSettings'
import {
  type AdvancedLists,
  type AdvancedWritePlan,
  buildAdvancedMatrixPayloadFromSetting,
  buildAdvancedRestoreMatrixPayload,
  buildAdvancedStandardWritePlan,
  buildAdvancedWritePlan,
  createEmptyDksSlot,
  createEmptyMtSlot,
  createEmptyTglSlot,
  hasAdvancedKeyFeatures,
  isAdvancedKeyType,
  parseAdvancedLayerMapToKeySettings,
} from '../utils/advancedKeyMapSettings'

const emptyAdvancedLists = (): AdvancedLists => ({
  dksList: [],
  mtList: [],
  tglList: [],
})

export function useMyKeysConf() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const isLoading = ref(false)
  const { t } = useLocaleStore()

  const isAdvancedRemap = computed(() =>
    hasAdvancedKeyFeatures(currentDevice.value?.manifest.capabilities),
  )

  /**
   * 获取当前层的原始 keyMap 数据。
   */
  const initKeys = async (isFn?: boolean, platform?: KeyPlatform): Promise<LayerKeysMap> => {
    isLoading.value = true
    const keyboardKeys = currentDevice.value?.features.keyboardKeys
    if (!keyboardKeys) {
      console.warn("Current device doesn't have keyboardKeys feature")
      return []
    }

    try {
      return await keyboardKeys.getLayerKeysMap(isFn, platform)
    } catch (error) {
      toast(t('toast.device.fail'))
      console.error('Failed to get keyboard keys', error)
      defaultReportError(`Failed to get keyboard keys ${JSON.stringify(error)}`)
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 设置指定键位数据（示例实现，后续按真实参数替换）。
   */
  const setKey = async (payload: keyMapItem, isFn?: boolean, platform?: KeyPlatform) => {
    const keyboardKeys = currentDevice.value?.features.keyboardKeys
    if (!keyboardKeys) {
      console.warn("Current device doesn't have keyboardKeys feature")
      return
    }

    try {
      await keyboardKeys.setLayerKeysMap(payload, isFn, platform)
    } catch (error) {
      console.error('Failed to get keyboard keys', error)
      defaultReportError(`Failed to get keyboard keys ${JSON.stringify(error)}`)
    }
  }

  const loadAdvancedLists = async (): Promise<AdvancedLists> => {
    const keyboardKeys = currentDevice.value?.features.keyboardKeys
    if (!keyboardKeys) return emptyAdvancedLists()

    try {
      const [dksList, mtList, tglList, travelList] = await Promise.all([
        keyboardKeys.getDksList(),
        keyboardKeys.getMtList(),
        keyboardKeys.getTglList(),
        currentDevice.value?.features.keyTravel?.getKeyTravel(0).catch(() => undefined),
      ])
      return { dksList, mtList, tglList, travelList }
    } catch (error) {
      console.error('Failed to get advanced key lists', error)
      defaultReportError(`Failed to get advanced key lists ${JSON.stringify(error)}`)
      return emptyAdvancedLists()
    }
  }

  const applyAdvancedWritePlan = async (
    plan: AdvancedWritePlan,
    isFn?: boolean,
    platform?: KeyPlatform,
  ) => {
    const keyboardKeys = currentDevice.value?.features.keyboardKeys
    if (!keyboardKeys) return

    for (const write of plan.listWrites) {
      if (write.kind === 'dks') await keyboardKeys.setDksByIndex(write.slot, write.index)
      else if (write.kind === 'mt') await keyboardKeys.setMtByIndex(write.slot, write.index)
      else await keyboardKeys.setTglByIndex(write.slot, write.index)
    }

    for (const item of plan.matrixItems) {
      await setKey(item, isFn, platform)
    }

    for (const clear of plan.listClears) {
      if (clear.kind === 'dks')
        await keyboardKeys.setDksByIndex(createEmptyDksSlot(clear.index), clear.index)
      else if (clear.kind === 'mt')
        await keyboardKeys.setMtByIndex(createEmptyMtSlot(clear.index), clear.index)
      else await keyboardKeys.setTglByIndex(createEmptyTglSlot(clear.index), clear.index)
    }
  }

  /**
   * 读取当前层并转成虚拟键盘用的 keySettings。
   * advanced 设备会额外拉 DKS/MT/TGL 列表再 join。
   */
  const initLayerKeySettings = async ({
    isFn,
    platform,
    layoutKeys,
    model,
    factoryKeys,
    displayOverrides,
    isFnLayer,
  }: {
    isFn?: boolean
    platform?: KeyPlatform
    layoutKeys: LayoutKey[][]
    model?: string
    /** 当前 Win/Mac 层的 Fn 出厂映射 */
    factoryKeys?: Record<string, string>
    displayOverrides?: KeyDisplayOverride[]
    isFnLayer?: boolean
  }): Promise<KeySetting[]> => {
    const keyMap = await initKeys(isFn, platform)

    if (isAdvancedRemap.value) {
      const advancedLists = await loadAdvancedLists()
      return parseAdvancedLayerMapToKeySettings({
        keyMap,
        layoutKeys,
        advancedLists,
        model,
        factoryKeys,
        displayOverrides,
        isFnLayer,
      })
    }

    return parseLayerMapToKeySettings({
      keyMap,
      layoutKeys,
      model,
      factoryKeys,
      displayOverrides,
      isFnLayer,
    })
  }

  /**
   * 下发一次改键。
   * advanced 设备：高级类型走列表+矩阵；常规/多媒体/宏只写矩阵（Type 已映射）。
   * @returns false 表示未下发（如高级槽位已满）
   */
  const commitMappedKey = async ({
    setting,
    existingSetting,
    isFn,
    platform,
  }: {
    setting: KeySetting
    existingSetting?: KeySetting
    isFn?: boolean
    platform?: KeyPlatform
  }): Promise<boolean> => {
    if (!isAdvancedRemap.value) {
      const payload = buildSdkKeyMapPayloadFromSetting(setting)
      if (!payload) return false
      await setKey(payload, isFn, platform)
      return true
    }

    try {
      if (isAdvancedKeyType(setting.keyType)) {
        const lists = await loadAdvancedLists()
        const plan = buildAdvancedWritePlan({ setting, lists, existingSetting })
        if (!plan) {
          toast.info(t('toast.keys.advancedSlotFull'))
          return false
        }
        await applyAdvancedWritePlan(plan, isFn, platform)
        return true
      }

      const payload = buildAdvancedMatrixPayloadFromSetting(setting)
      if (!payload) return false

      if (isAdvancedKeyType(existingSetting?.keyType)) {
        await applyAdvancedWritePlan(
          buildAdvancedStandardWritePlan({ matrixItem: payload, existingSetting }),
          isFn,
          platform,
        )
      } else {
        await setKey(payload, isFn, platform)
      }
      return true
    } catch (error) {
      console.error('Failed to commit mapped key', error)
      defaultReportError(`Failed to commit mapped key ${JSON.stringify(error)}`)
      toast(t('toast.device.fail'))
      return false
    }
  }

  /**
   * 恢复默认键。advanced 设备会把 Type 映射到 GCOME，并清掉占用的高级 slot。
   */
  const restoreMappedKey = async ({
    setting,
    defaultKey,
    factoryKeywords,
    isFn,
    platform,
  }: {
    setting?: KeySetting
    defaultKey: {
      keywords?: string
      type?: unknown
      code?: unknown
      confIndex?: unknown
      key?: unknown
    }
    factoryKeywords?: string
    isFn?: boolean
    platform?: KeyPlatform
  }) => {
    if (!isAdvancedRemap.value) {
      const payload = buildRestoreKeyMapPayload({ setting, defaultKey, factoryKeywords })
      if (payload) await setKey(payload, isFn, platform)
      return payload
    }

    const payload = buildAdvancedRestoreMatrixPayload({ setting, defaultKey, factoryKeywords })
    if (!payload) return undefined

    if (isAdvancedKeyType(setting?.keyType)) {
      await applyAdvancedWritePlan(
        buildAdvancedStandardWritePlan({ matrixItem: payload, existingSetting: setting }),
        isFn,
        platform,
      )
      return payload
    }

    await setKey(payload, isFn, platform)
    return payload
  }

  // 获取宏列表
  const getMacroList = async () => {
    const keyboardKeys = currentDevice.value?.features.keyboardKeys
    if (!keyboardKeys) {
      console.warn("Current device doesn't have keyboardKeys feature")
      return
    }
    try {
      const result = await keyboardKeys.getMacroList()
      console.log('Macro list:', result)
    } catch (error) {
      console.error('Failed to get macro list', error)
      defaultReportError(`Failed to get macro list ${JSON.stringify(error)}`)
    }
  }

  const setMacroByIndex = async (macro: Macro, index: number) => {
    const keyboardKeys = currentDevice.value?.features.keyboardKeys
    if (!keyboardKeys) {
      console.log("Current device doesn't have keyboardKeys feature")
      return
    }

    try {
      const result = await keyboardKeys.setMacroByIndex(macro, index)
      console.log('Macro by index:', result)
    } catch (error) {
      defaultReportError(`Failed to set macro by index ${JSON.stringify(error)}`)
    }
  }

  return {
    isLoading,
    isAdvancedRemap,
    initKeys,
    setKey,
    initLayerKeySettings,
    commitMappedKey,
    restoreMappedKey,
    getMacroList,
    setMacroByIndex,
  }
}
